import { useState } from "react";
import {
  CopyButton,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, WIDGET_H2 } from "@/components/tool-kit/styles";
import { formatK } from "@/components/tools/chem-physical/gibbs-panel";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import type { NERNST_EQUATION_CALCULATOR_STRINGS } from "@/components/tools/nernst-equation-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type NernstSolution,
  type NernstTerm,
  solveNernst,
  VOLTAGE_UNITS,
} from "@/lib/tool-kit/chem-physical/electro";
import {
  TEMPERATURE_UNITS,
  temperatureSymbol,
} from "@/lib/tool-kit/chem-physical/units";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

export type NernstStrings = typeof NERNST_EQUATION_CALCULATOR_STRINGS;

export function electroMessages(
  strings: NernstStrings
): Record<string, string> {
  return {
    missing: strings.enterValues,
    unreadable: strings.errorUnreadable,
    nonPositive: strings.errorNonPositive,
    belowZero: strings.errorBelowZero,
    badN: strings.errorBadN,
    zeroLogQ: strings.errorZeroLogQ,
    resultBelowZero: strings.errorResultBelowZero,
    signMismatch: strings.errorSignMismatch,
    zeroValue: strings.errorZeroValue,
  };
}

const DEFAULT_SIG = 3;
const DEAD_CELL_VOLTS = 1e-9;

export function cellState(volts: number): "dead" | "galvanic" | "electrolytic" {
  if (Math.abs(volts) < DEAD_CELL_VOLTS) {
    return "dead";
  }
  return volts > 0 ? "galvanic" : "electrolytic";
}
const TERMS: NernstTerm[] = ["e", "e0", "q", "t"];
const VOLT_OPTIONS = VOLTAGE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));
const EXAMPLES = [
  { id: "exampleDaniell", e0: "1.10", n: "2", q: "10.0" },
  { id: "exampleHydrogen", e0: "0.00", n: "2", q: "1.0e14" },
] as const;

function NernstAnswer({
  solution,
  texts,
  units,
  sig,
  strings,
}: {
  solution: NernstSolution;
  texts: { n: string } & Record<NernstTerm, string>;
  units: { e: string; e0: string; t: string };
  sig: number;
  strings: NernstStrings;
}) {
  const fmt = useToolNumber();
  const { unknown } = solution;
  const p = (value: number) => formatPlain(fmt, value);
  const labels: Record<NernstTerm, string> = {
    e: strings.termE,
    e0: strings.termE0,
    q: strings.termQ,
    t: strings.termT,
  };
  const voltText = (volts: number, unit: string) =>
    `${formatSig(fmt, volts / (VOLTAGE_UNITS.find((u) => u.id === unit)?.factor ?? 1), sig)} ${unit}`;
  const answerText = {
    e: voltText(solution.e, units.e),
    e0: voltText(solution.e0, units.e0),
    q: formatK(fmt, solution.log10Q, sig),
    t: `${formatSig(fmt, solution.answer, sig)} ${temperatureSymbol(units.t)}`,
  }[unknown];
  const slopeMillivolts = solution.slope * Math.LN10 * 1000;
  const spontaneity = {
    dead: strings.cellDead,
    galvanic: strings.galvanic,
    electrolytic: strings.nonSpontaneousCell,
  }[cellState(solution.e)];
  const conversions: string[] = [];
  for (const term of ["e", "e0"] as const) {
    if (term !== unknown && units[term] !== "V") {
      conversions.push(
        t(strings.convertLine, {
          label: labels[term],
          value: texts[term].trim(),
          unit: units[term],
          converted: `${p(term === "e" ? solution.e : solution.e0)} V`,
        })
      );
    }
  }
  if (unknown !== "t" && units.t !== "K") {
    conversions.push(
      t(strings.convertLine, {
        label: labels.t,
        value: texts.t.trim(),
        unit: temperatureSymbol(units.t),
        converted: `${p(solution.kelvin)} K`,
      })
    );
  }
  const rearranged = {
    e: strings.equation,
    e0: strings.rearrangedE0,
    q: strings.rearrangedQ,
    t: strings.rearrangedT,
  }[unknown];
  const substituted = t(
    {
      e: strings.substitutedE,
      e0: strings.substitutedE0,
      q: strings.substitutedQ,
      t: strings.substitutedT,
    }[unknown],
    {
      e: `${p(solution.e)} V`,
      e0: `${p(solution.e0)} V`,
      slope: p(solution.slope),
      lnq: p(solution.lnQ),
      t: p(solution.kelvin),
      n: p(solution.n),
      result: {
        e: `${formatSig(fmt, solution.e, sig + 1)} V`,
        e0: `${formatSig(fmt, solution.e0, sig + 1)} V`,
        q: formatK(fmt, solution.log10Q, sig + 1),
        t: `${formatSig(fmt, solution.kelvin, sig + 1)} K`,
      }[unknown],
    }
  );
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={labels[unknown]} value={answerText} />
        <StatCard
          label={strings.deltaG}
          value={`${formatSig(fmt, solution.dg / 1000, sig)} kJ/mol`}
        />
        <StatCard label={strings.cellState} value={spontaneity} />
        <StatCard
          label={strings.slopeLabel}
          value={`${formatSig(fmt, slopeMillivolts, sig)} mV`}
        />
        <StatCard
          label={strings.log10K}
          value={formatSig(fmt, solution.log10K, sig)}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.equation}</p>
        {unknown === "e" ? null : <p>{rearranged}</p>}
        {conversions.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>
          {t(strings.slopeLine, {
            t: p(solution.kelvin),
            n: p(solution.n),
            slope: p(solution.slope),
          })}
        </p>
        {unknown === "q" || unknown === "t" ? null : (
          <p>
            {t(strings.lnQLine, { q: texts.q.trim(), lnq: p(solution.lnQ) })}
          </p>
        )}
        <p>{substituted}</p>
        <p>
          {t(strings.deltaGLine, {
            n: p(solution.n),
            e: p(solution.e),
            dg: p(solution.dg),
          })}
        </p>
        <p>{strings.constantsNote}</p>
      </div>
      <Notice>{strings.slopeNote}</Notice>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: labels[unknown],
          value: answerText,
        })}
      />
    </>
  );
}

export function NernstPanel({
  strings,
  sigChoice,
}: {
  strings: NernstStrings;
  sigChoice: string;
}) {
  const [unknown, setUnknown] = useState<NernstTerm>("e");
  const [n, setN] = useState("2");
  const [text, setText] = useState<Record<NernstTerm, string>>({
    e: "",
    e0: "1.10",
    q: "10.0",
    t: "25.0",
  });
  const [unit, setUnit] = useState({ e: "V", e0: "V", t: "C" });
  const outcome = solveNernst({ unknown, n, text, unit });
  // The electron count is an exact integer, so it never limits the significant figures.
  const typed = TERMS.filter((term) => term !== unknown).map(
    (term) => text[term]
  );
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(typed, DEFAULT_SIG)
      : Number(sigChoice);
  const labels: Record<NernstTerm, string> = {
    e: strings.termE,
    e0: strings.termE0,
    q: strings.termQ,
    t: strings.termT,
  };
  const allLabels: Record<string, string> = { ...labels, n: strings.termN };
  const fill = (example: (typeof EXAMPLES)[number]) => {
    setUnknown("e");
    setN(example.n);
    setUnit({ e: "V", e0: "V", t: "C" });
    setText({ e: "", e0: example.e0, q: example.q, t: "25.0" });
  };
  const field = (term: NernstTerm) => {
    const label =
      unknown === term
        ? t(strings.answerSuffix, { label: labels[term] })
        : labels[term];
    if (term === "q") {
      return unknown === "q" ? (
        <div className="space-y-2" key={term}>
          <p className={LABEL}>{label}</p>
          <p className="rounded-md border border-primary/40 border-dashed bg-primary/5 px-3 py-2 text-foreground text-sm">
            {strings.solvedCell}
          </p>
        </div>
      ) : (
        <TextField
          hint={strings.qHint}
          id="nern-q"
          key={term}
          label={label}
          numeric
          onChange={(value) => setText({ ...text, q: value })}
          value={text.q}
        />
      );
    }
    const isVoltage = term === "e" || term === "e0";
    return (
      <QuantityInput
        id={`nern-${term}`}
        key={term}
        label={label}
        onUnitChange={(value) => setUnit({ ...unit, [term]: value })}
        onValueChange={(value) => setText({ ...text, [term]: value })}
        solved={unknown === term}
        unit={unit[term as "e" | "e0" | "t"]}
        unitLabel={isVoltage ? strings.unitVoltage : strings.unitTemperature}
        units={isVoltage ? VOLT_OPTIONS : TEMPERATURE_OPTIONS}
        value={text[term]}
      />
    );
  };
  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="nern-solve"
        onChange={setUnknown}
        options={[
          { value: "e", label: strings.termE },
          { value: "e0", label: strings.termE0 },
          { value: "q", label: strings.termQ },
          { value: "t", label: strings.termT },
        ]}
        value={unknown}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {EXAMPLES.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example.id}
            onClick={() => fill(example)}
            type="button"
          >
            {strings[example.id]}
          </button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.nHint}
          id="nern-n"
          label={strings.termN}
          numeric
          onChange={setN}
          value={n}
        />
        {TERMS.map(field)}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <NernstAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={{ ...text, n }}
            units={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={allLabels}
            messages={electroMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
